import { useState } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

export default function LoremIpsumGenerator() {
    const { t } = useI18n();
    const [type, setType] = useState<'paragraphs' | 'sentences' | 'words'>('paragraphs');
    const [count, setCount] = useState(3);
    const [startWithLorem, setStartWithLorem] = useState(true);
    const [generated, setGenerated] = useState('');

    useSEO({
        title: 'Lorem Ipsum Generator',
        description: 'Generate placeholder text for designs and mockups',
        keywords: ['lorem ipsum', 'placeholder', 'dummy text', 'generator', 'filler text'],
    });

    const loremWords = [
        'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
        'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
        'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
        'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
        'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
        'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
        'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
        'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum'
    ];

    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const generateWords = (num: number, start?: boolean): string => {
        const words: string[] = [];

        if (start && startWithLorem) {
            words.push('Lorem', 'ipsum', 'dolor', 'sit', 'amet');
            num = Math.max(0, num - 5);
        }

        for (let i = 0; i < num; i++) {
            const word = loremWords[Math.floor(Math.random() * loremWords.length)];
            if (word) words.push(word);
        }

        return words.join(' ');
    };

    const generateSentence = (start?: boolean): string => {
        const wordCount = Math.floor(Math.random() * 10) + 8; // 8-17 words
        const sentence = generateWords(wordCount, start);
        return capitalize(sentence) + '.';
    };

    const generateParagraph = (start?: boolean): string => {
        const sentenceCount = Math.floor(Math.random() * 4) + 4; // 4-7 sentences
        const sentences: string[] = [];

        for (let i = 0; i < sentenceCount; i++) {
            sentences.push(generateSentence(start && i === 0));
        }

        return sentences.join(' ');
    };

    const generate = () => {
        let result = '';

        switch (type) {
            case 'paragraphs':
                const paragraphs: string[] = [];
                for (let i = 0; i < count; i++) {
                    paragraphs.push(generateParagraph(i === 0));
                }
                result = paragraphs.join('\n\n');
                break;

            case 'sentences':
                const sentences: string[] = [];
                for (let i = 0; i < count; i++) {
                    sentences.push(generateSentence(i === 0));
                }
                result = sentences.join(' ');
                break;

            case 'words':
                result = generateWords(count, true);
                break;
        }

        setGenerated(result);
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(generated);
            alert('Copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Lorem Ipsum Generator
                </h1>
                <p className="text-gray-600">Generate placeholder text for your designs and mockups</p>
            </div>

            <div className="space-y-6">
                {/* Controls */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    {/* Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Generate
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {(['paragraphs', 'sentences', 'words'] as const).map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setType(t)}
                                    className={`py-2 px-4 rounded-lg border-2 transition-colors capitalize ${type === t
                                            ? 'border-primary-500 bg-primary-50 text-primary-700 font-semibold'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Count */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Number of {type}: {count}
                        </label>
                        <input
                            type="range"
                            min="1"
                            max={type === 'words' ? 500 : type === 'sentences' ? 50 : 20}
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                            className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>1</span>
                            <span>{type === 'words' ? 500 : type === 'sentences' ? 50 : 20}</span>
                        </div>
                    </div>

                    {/* Options */}
                    <div>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={startWithLorem}
                                onChange={(e) => setStartWithLorem(e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700">
                                Start with "Lorem ipsum dolor sit amet"
                            </span>
                        </label>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={generate}
                        className="btn-primary w-full"
                    >
                        ✨ Generate
                    </button>
                </div>

                {/* Output */}
                {generated && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Generated Text ({generated.split(' ').length} words)
                            </label>
                            <button
                                onClick={copyToClipboard}
                                className="btn-secondary text-sm"
                            >
                                📋 Copy
                            </button>
                        </div>
                        <textarea
                            value={generated}
                            readOnly
                            className="input-field"
                            rows={12}
                        />
                    </div>
                )}

                {/* About Lorem Ipsum */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h3 className="font-semibold text-primary-900 mb-2">
                        💡 About Lorem Ipsum
                    </h3>
                    <div className="space-y-2 text-sm text-primary-800">
                        <p>
                            Lorem Ipsum is a placeholder text commonly used to demonstrate the visual form
                            of a document or a typeface without relying on meaningful content.
                        </p>
                        <p>
                            It's derived from sections 1.10.32 and 1.10.33 of "de Finibus Bonorum et Malorum"
                            (The Extremes of Good and Evil) by Cicero, written in 45 BC.
                        </p>
                        <p className="font-medium">
                            Use cases: Web design mockups, typography samples, content placeholders,
                            layout testing, and more.
                        </p>
                    </div>
                </div>

                {/* Quick Examples */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">
                        📝 Quick Examples
                    </h3>
                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                setType('paragraphs');
                                setCount(3);
                                setStartWithLorem(true);
                                setTimeout(generate, 100);
                            }}
                            className="w-full text-left px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                        >
                            <div className="font-medium text-gray-900">Short Article</div>
                            <div className="text-sm text-gray-600">3 paragraphs</div>
                        </button>
                        <button
                            onClick={() => {
                                setType('sentences');
                                setCount(10);
                                setStartWithLorem(true);
                                setTimeout(generate, 100);
                            }}
                            className="w-full text-left px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                        >
                            <div className="font-medium text-gray-900">Product Description</div>
                            <div className="text-sm text-gray-600">10 sentences</div>
                        </button>
                        <button
                            onClick={() => {
                                setType('words');
                                setCount(50);
                                setStartWithLorem(false);
                                setTimeout(generate, 100);
                            }}
                            className="w-full text-left px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                        >
                            <div className="font-medium text-gray-900">Headline / Title</div>
                            <div className="text-sm text-gray-600">50 words</div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
